/**
 * Inactivity Provider
 *
 * Only runs while the user is authenticated. Stops timers/modals on logout
 * so the countdown cannot continue on the login screen.
 */

import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useInactivityTimer } from '@/shared/hooks/useInactivityTimer';
import InactivityWarningModal from '@/shared/components/InactivityWarningModal.jsx';
import { tokenService } from '@/features/auth/services/tokenService.js';
import { logger } from '@/shared/utils/logger.js';

const InactivityContext = createContext(null);

export const useInactivity = () => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error('useInactivity must be used within an InactivityProvider');
  }
  return context;
};

const readIsAuthenticated = () => {
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    if (localStorage.getItem('isLoggingOut') === 'true') {
      return false;
    }
    return tokenService.isAuthenticated();
  } catch {
    return false;
  }
};

const InactivityProvider = ({
  children,
  timeoutMinutes = 5,
  warningMinutes = 2,
  enabled: enabledProp = true,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(readIsAuthenticated);
  const [isLoggingOutUi, setIsLoggingOutUi] = useState(false);

  useEffect(() => {
    const syncAuth = () => {
      setIsAuthenticated(readIsAuthenticated());
    };

    syncAuth();

    const onStorage = (event) => {
      if (
        !event.key ||
        event.key === 'isAuthenticated' ||
        event.key === 'token' ||
        event.key === 'accessToken' ||
        event.key === 'isLoggingOut'
      ) {
        syncAuth();
      }
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', syncAuth);
    document.addEventListener('visibilitychange', syncAuth);

    // Catch same-tab auth changes (login/logout) that do not fire `storage`
    const intervalId = window.setInterval(syncAuth, 2000);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', syncAuth);
      document.removeEventListener('visibilitychange', syncAuth);
      window.clearInterval(intervalId);
    };
  }, []);

  const timerEnabled = Boolean(enabledProp && isAuthenticated && !isLoggingOutUi);

  const handleTimeout = useCallback(async () => {
    logger.warn('Session timeout - logging out user due to inactivity');
    setIsLoggingOutUi(true);
    setIsAuthenticated(false);

    try {
      await tokenService.safeLogout();
    } catch (error) {
      logger.error('Error during inactivity logout', error);
    } finally {
      if (typeof window !== 'undefined') {
        window.location.replace('/login?reason=timeout');
      }
    }
  }, []);

  const handleWarning = useCallback(() => {
    logger.info('Session timeout warning triggered');
  }, []);

  const {
    isWarningVisible,
    timeRemaining,
    formattedTimeRemaining,
    warningDurationMs,
    isLoggingOut,
    extendSession,
    forceLogout,
  } = useInactivityTimer({
    timeoutMinutes,
    warningMinutes,
    onWarning: handleWarning,
    onTimeout: handleTimeout,
    enabled: timerEnabled,
  });

  const handleExtendSession = useCallback(() => {
    if (isLoggingOut || isLoggingOutUi) {
      return;
    }
    logger.info('User extended session from warning modal');
    extendSession();
  }, [extendSession, isLoggingOut, isLoggingOutUi]);

  const handleLogoutNow = useCallback(() => {
    if (isLoggingOut || isLoggingOutUi) {
      return;
    }
    logger.info('User chose to logout immediately from warning modal');
    forceLogout();
  }, [forceLogout, isLoggingOut, isLoggingOutUi]);

  const contextValue = {
    isWarningVisible,
    timeRemaining,
    formattedTimeRemaining,
    timeoutMinutes,
    warningMinutes,
    enabled: timerEnabled,
    isLoggingOut: isLoggingOut || isLoggingOutUi,
    extendSession: handleExtendSession,
    forceLogout: handleLogoutNow,
    resetTimers: extendSession,
  };

  const showModal =
    timerEnabled &&
    isWarningVisible &&
    !isLoggingOut &&
    !isLoggingOutUi;

  return (
    <InactivityContext.Provider value={contextValue}>
      {children}
      <InactivityWarningModal
        isVisible={showModal}
        timeRemaining={timeRemaining}
        formattedTimeRemaining={formattedTimeRemaining}
        warningDurationMs={warningDurationMs}
        onExtendSession={handleExtendSession}
        onLogoutNow={handleLogoutNow}
      />
    </InactivityContext.Provider>
  );
};

export default InactivityProvider;
